import { useCallback, useEffect, useRef } from 'react';

/**
 * On-demand paging for a windowed table (the blotter, the counterparty register).
 * Auto-fills an underfilled viewport — a short window below scrollable height
 * would strand matches on unloaded pages — bounded by `maxAutoFillPages` per
 * query (see `resetKey`).
 */
const ROW_PX = 48;

export interface InfiniteScrollLoaderOptions {
  scrollRef: React.RefObject<HTMLElement | null>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** Truthy when the last fetch errored — blocks AUTO-fill (else hasNextPage,
   *  still true off the last success, would let auto-fill storm the endpoint). A
   *  user scroll to the bottom is NOT blocked, so an explicit gesture can retry. */
  error?: unknown;
  /** Must return the fetch promise — the lock releases on its settle. Typed
   *  Promise-returning so a `() => { fetchNextPage(); }` refactor (which resolves
   *  immediately and reintroduces burst-firing) is a compile error, not a silent
   *  regression. */
  onLoadMore?: () => Promise<unknown>;
  /** Rendered (client-filtered) row count — re-checks viewport fill when it changes. */
  rowCount: number;
  /** Identity of the current query (filters/search/sort/CP/mode). Changing it
   *  resets the per-query auto-fill budget — without this the budget is per-SESSION
   *  (the table isn't re-keyed) and dies after ~maxAutoFillPages filter switches. */
  resetKey?: unknown;
  fillThresholdPx?: number;
  bottomThresholdPx?: number;
  maxAutoFillPages?: number;
}

export function useInfiniteScrollLoader({
  scrollRef,
  hasNextPage,
  isFetchingNextPage,
  error,
  onLoadMore,
  rowCount,
  resetKey,
  fillThresholdPx = ROW_PX,
  bottomThresholdPx = ROW_PX * 2,
  maxAutoFillPages = 20,
}: InfiniteScrollLoaderOptions): void {
  const lockRef = useRef(false);
  const autoFillCountRef = useRef(0);

  // Dispatch a page load. Returns true iff a load was actually started (used to
  // charge the auto-fill budget only for real requests). NB: no `error` guard here
  // — the auto-fill effect carries it, so an explicit scroll can retry a transient
  // failure.
  const requestLoadMore = useCallback((): boolean => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage || lockRef.current) return false;
    lockRef.current = true;
    try {
      // Release on settle, NOT on an observed isFetchingNextPage transition — react
      // batching can collapse false→true→false and strand an effect-based release.
      Promise.resolve(onLoadMore())
        .catch(() => {})
        .finally(() => {
          lockRef.current = false;
        });
    } catch {
      // A synchronous throw would otherwise strand the lock true forever.
      lockRef.current = false;
      return false;
    }
    return true;
  }, [onLoadMore, hasNextPage, isFetchingNextPage]);

  // A new query gets a fresh auto-fill budget (kvi-r2 #2).
  useEffect(() => {
    autoFillCountRef.current = 0;
  }, [resetKey]);

  // A user scroll to the bottom is genuine intent → resets the auto-fill budget and
  // may retry after an error.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - bottomThresholdPx) {
        autoFillCountRef.current = 0;
        requestLoadMore();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef, requestLoadMore, bottomThresholdPx]);

  // Auto-fill an underfilled viewport (re-runs as rows arrive / fetches settle).
  // Charge the budget only when a load actually starts.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || error || !hasNextPage || isFetchingNextPage) return;
    if (autoFillCountRef.current >= maxAutoFillPages) return;
    if (el.scrollHeight <= el.clientHeight + fillThresholdPx && requestLoadMore()) {
      autoFillCountRef.current += 1;
    }
    // resetKey: re-run after a query switch even if rowCount/isFetchingNextPage didn't
    // change (a cache-hot new query), so the just-reset budget is actually spent (kvi-r3 #7).
  }, [scrollRef, rowCount, hasNextPage, isFetchingNextPage, error, requestLoadMore, fillThresholdPx, maxAutoFillPages, resetKey]);
}
